<template>
    <main class="cb-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/company/citizens" class="tbv2-crumb">{{ $t('menu.companies.citizens') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ $t('company.citizens.create') }}</span>
            </div>
        </portal>

        <header class="cb-hero">
            <router-link to="/company/citizens" class="cb-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') }}</span>
            </router-link>
            <h1 class="cb-hero-title">{{ $t('company.citizens.create') }}</h1>
            <p class="cb-hero-sub">{{ $t('company.citizens.create_desc') }}</p>
        </header>

        <section class="cb-card">
            <header class="cb-card-header">
                <div class="cb-card-icon">
                    <i class="fal fa-user-plus"></i>
                </div>
                <div>
                    <h2 class="cb-card-title">{{ $t('company.citizens.identity') }}</h2>
                    <p class="cb-card-desc">{{ $t('company.citizens.identity_desc') }}</p>
                </div>
            </header>

            <form class="cb-form" @submit.prevent="submit">
                <div class="cb-grid">
                    <div class="cb-field cb-field--full">
                        <label class="cb-label" for="citizen-name">{{ $t('companies.name') }}</label>
                        <input
                            id="citizen-name"
                            v-model="citizen.name"
                            type="text"
                            class="cb-input"
                            autocomplete="off"
                        />
                        <p v-if="errorText('name')" class="cb-error">{{ errorText('name') }}</p>
                    </div>

                    <div class="cb-field">
                        <label class="cb-label" for="citizen-email">{{ $t('companies.email') }}</label>
                        <input
                            id="citizen-email"
                            v-model="citizen.email"
                            type="email"
                            class="cb-input"
                            autocomplete="off"
                        />
                        <p v-if="errorText('email')" class="cb-error">{{ errorText('email') }}</p>
                    </div>

                    <div class="cb-field">
                        <label class="cb-label" for="citizen-phone">{{ $t('companies.phone') }}</label>
                        <input
                            id="citizen-phone"
                            v-model="citizen.phone"
                            type="text"
                            class="cb-input"
                            placeholder="+32470123456"
                            autocomplete="off"
                        />
                        <p v-if="errorText('phone')" class="cb-error">{{ errorText('phone') }}</p>
                    </div>
                </div>

                <div class="cb-section-divider">
                    <span class="cb-section-label">{{ $t('company.citizens.role') }}</span>
                </div>

                <div class="cb-field cb-field--full">
                    <div class="cb-roles">
                        <label
                            v-for="role in roles"
                            :key="role"
                            class="cb-role-card"
                            :class="{
                                'is-selected': citizen.role === role,
                                'is-disabled': isRoleLocked(role)
                            }"
                        >
                            <input
                                type="radio"
                                class="cb-role-input"
                                :value="role"
                                :disabled="isRoleLocked(role)"
                                v-model="citizen.role"
                            />
                            <div class="cb-role-header">
                                <span class="cb-role-radio" :class="{ 'is-checked': citizen.role === role }">
                                    <i v-if="citizen.role === role" class="fal fa-check"></i>
                                </span>
                                <i :class="roleIcon(role)" class="cb-role-icon"></i>
                                <span class="cb-role-name">{{ $t('company.citizens.roles.' + role) }}</span>
                                <span v-if="isRoleLocked(role)" class="cb-role-lock">
                                    <i class="fal fa-lock"></i>
                                    {{ $t('company.citizens.role_locked_badge') }}
                                </span>
                            </div>
                            <p class="cb-role-desc">{{ $t('company.citizens.roles_desc.' + role) }}</p>
                        </label>
                    </div>
                    <p class="cb-roles-note">
                        <i class="fal fa-info-circle"></i>
                        {{ $t('company.citizens.role_locked_hint') }}
                    </p>
                    <p v-if="errorText('role')" class="cb-error">{{ errorText('role') }}</p>
                </div>

                <div class="cb-section-divider">
                    <span class="cb-section-label">{{ $t('company.citizens.password') }}</span>
                </div>

                <div class="cb-field cb-field--full">
                    <label class="cb-label" for="citizen-password">{{ $t('company.citizens.password') }}</label>
                    <div class="cb-password">
                        <input
                            id="citizen-password"
                            v-model="citizen.password"
                            :type="showPassword ? 'text' : 'password'"
                            class="cb-input"
                            autocomplete="new-password"
                        />
                        <button
                            type="button"
                            class="cb-password-toggle"
                            :title="showPassword ? $t('company.citizens.password_hide') : $t('company.citizens.password_show')"
                            @click="showPassword = !showPassword"
                        >
                            <i :class="showPassword ? 'fal fa-eye-slash' : 'fal fa-eye'"></i>
                        </button>
                    </div>
                    <small class="cb-hint">{{ $t('company.citizens.password_hint') }}</small>
                    <p v-if="errorText('password')" class="cb-error">{{ errorText('password') }}</p>
                </div>

                <div class="cb-field cb-field--full">
                    <label class="cb-label" for="citizen-password-confirmation">
                        {{ $t('company.citizens.password_confirmation') }}
                    </label>
                    <div class="cb-password">
                        <input
                            id="citizen-password-confirmation"
                            v-model="citizen.password_confirmation"
                            :type="showPassword ? 'text' : 'password'"
                            class="cb-input"
                            autocomplete="new-password"
                        />
                    </div>
                    <p v-if="errorText('password_confirmation')" class="cb-error">
                        {{ errorText('password_confirmation') }}
                    </p>
                </div>

                <div class="cb-actions">
                    <router-link to="/company/citizens" class="cb-btn cb-btn--ghost">
                        {{ $t('common.cancel') }}
                    </router-link>
                    <button type="submit" class="cb-btn cb-btn--primary" :disabled="loading">
                        <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else class="fal fa-check"></i>
                        {{ $t('company.citizens.create_submit') }}
                    </button>
                </div>
            </form>
        </section>
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import { CITIZEN_ROLES, CITIZEN_ROLE_DEFAULT, CITIZEN_ROLE_ICONS } from '@/config/citizenRoles'

const PASSWORD_MIN_LENGTH = 8

// The plain `citizen` role can't be created from the back-office: such an
// account only exists once the citizen has registered by themselves.
const LOCKED_ROLES = [CITIZEN_ROLE_DEFAULT]

export default {
    name: 'CitizenBuilder',
    data() {
        return {
            loading: false,
            showPassword: false,
            error: {},
            roles: CITIZEN_ROLES
        }
    },
    computed: {
        ...mapState('company', {
            citizen: (state) => state.newCitizen
        })
    },
    methods: {
        ...mapActions('company', ['createCitizen']),
        roleIcon(role) {
            return CITIZEN_ROLE_ICONS[role] || 'fal fa-user'
        },
        isRoleLocked(role) {
            return LOCKED_ROLES.includes(role)
        },
        // Backend errors come as { field: [message] }. When the backend answers
        // with a rule name (`confirmed`, `unique`, …) rather than a sentence, it
        // is resolved against the error.validation namespace.
        errorText(field) {
            const raw = this.error[field]
            if (!raw) return ''

            const message = Array.isArray(raw) ? raw[0] : raw

            for (const key of [`error.validation.${message}`, `error.${message}`]) {
                const translated = this.$t(key)
                if (translated !== key) return translated
            }

            return message
        },
        validate() {
            const error = {}

            if (!this.citizen.name || !this.citizen.name.trim()) {
                error.name = this.$t('company.citizens.errors.name_required')
            }
            if (!this.citizen.email || !/^\S+@\S+\.\S+$/.test(this.citizen.email)) {
                error.email = this.$t('error.validation.mail_valid')
            }
            if (!this.citizen.password || this.citizen.password.length < PASSWORD_MIN_LENGTH) {
                error.password = this.$t('company.citizens.errors.password_short', { min: PASSWORD_MIN_LENGTH })
            } else if (this.citizen.password !== this.citizen.password_confirmation) {
                error.password_confirmation = this.$t('error.validation.confirmed')
            }
            if (!CITIZEN_ROLES.includes(this.citizen.role) || this.isRoleLocked(this.citizen.role)) {
                error.role = this.$t('company.citizens.errors.role_required')
            }

            this.error = error
            return Object.keys(error).length === 0
        },
        submit() {
            if (!this.validate()) return

            this.loading = true
            this.createCitizen(this.citizen)
                .then(() => {
                    this.error = {}
                    this.resetForm()
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('company.citizens.create_success')
                    })
                    this.$router.push('/company/citizens')
                })
                .catch((e) => {
                    this.error = e && typeof e === 'object' && !e.message ? e : {}
                    this.$store.dispatch('toast', {
                        type: 'error',
                        title: this.$t('company.citizens.create_error')
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },
        resetForm() {
            this.$store.commit('company/setNewCitizen', {
                name: null,
                email: null,
                phone: null,
                password: null,
                password_confirmation: null,
                role: 'technical_agent'
            })
        }
    },
    beforeDestroy() {
        this.resetForm()
    }
}
</script>

<style scoped>
.cb-v2 {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.cb-hero {
    margin-bottom: var(--v2-space-5);
}

.cb-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.cb-back:hover {
    color: var(--v2-primary);
}

.cb-back i {
    font-size: 0.75rem;
}

.cb-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.cb-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.cb-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.cb-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.cb-card-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.cb-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.cb-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.cb-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.cb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.cb-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cb-field--full {
    grid-column: 1 / -1;
}

.cb-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.cb-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.cb-input:hover {
    border-color: var(--v2-border-strong);
}

.cb-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.cb-input::placeholder {
    color: var(--v2-text-4);
}

.cb-hint {
    font-size: 0.72rem;
    color: var(--v2-text-3);
}

/* Password */
.cb-password {
    position: relative;
}

.cb-password .cb-input {
    padding-right: 40px;
}

.cb-password-toggle {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border: none;
    border-radius: var(--v2-radius-sm, 6px);
    background: transparent;
    color: var(--v2-text-3);
    cursor: pointer;
    font-size: 0.78rem;
}

.cb-password-toggle:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.cb-section-divider {
    display: flex;
    align-items: center;
    padding-top: 6px;
    margin-top: 2px;
    border-top: 1px solid var(--v2-border-subtle);
}

.cb-section-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 14px;
}

/* Role cards */
.cb-roles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
}

/* The global form reset (assets/css/bmv/components/form.css) forces
   `line-height: 1` and `width: 99%` on every <label> — overridden here. */
.cb-role-card {
    position: relative;
    padding: 14px 16px !important;
    width: 100%;
    box-sizing: border-box;
    line-height: 1.45;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    display: block;
}

.cb-role-card:hover {
    border-color: var(--v2-border-strong);
}

.cb-role-card.is-selected {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.cb-role-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}

/* Locked role: created through citizen self-registration only */
.cb-role-card.is-disabled,
.cb-role-card.is-disabled:hover {
    cursor: not-allowed;
    opacity: 0.55;
    background: var(--v2-surface-2);
    border-color: var(--v2-border);
}

.cb-role-card.is-disabled .cb-role-name,
.cb-role-card.is-disabled .cb-role-icon {
    color: var(--v2-text-3);
}

.cb-role-lock {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    color: var(--v2-text-3);
    font-size: 0.62rem;
    font-weight: var(--v2-font-weight-semibold);
    white-space: nowrap;
}

.cb-role-lock i {
    font-size: 0.58rem;
}

.cb-roles-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 10px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    line-height: 1.45;
}

.cb-roles-note i {
    margin-top: 2px;
    flex-shrink: 0;
}

.cb-role-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.cb-role-radio {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid var(--v2-border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all var(--v2-transition-fast);
    background: var(--v2-surface-0);
}

.cb-role-radio i {
    color: #fff !important;
    font-size: 0.6rem;
}

.cb-role-radio.is-checked {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
}

.cb-role-icon {
    color: var(--v2-text-3);
    font-size: 0.82rem;
}

.cb-role-card.is-selected .cb-role-icon {
    color: var(--v2-primary);
}

.cb-role-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    line-height: 1.35;
}

.cb-role-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin: 0;
    line-height: 1.4;
    padding-left: 26px;
}

.cb-error {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-danger);
    font-weight: var(--v2-font-weight-medium);
}

/* Actions */
.cb-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.cb-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.cb-btn i {
    font-size: 0.8rem;
}

.cb-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.cb-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.cb-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.cb-btn--primary i {
    color: #fff !important;
}

.cb-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.cb-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

@media (max-width: 900px) {
    .cb-v2 {
        padding: 16px 18px 32px;
    }

    .cb-hero-title {
        font-size: 1.375rem;
    }

    .cb-grid {
        grid-template-columns: 1fr;
    }

    .cb-form {
        padding: 16px;
    }

    .cb-card-header {
        padding: 14px 16px;
    }

    .cb-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }

    .cb-btn {
        justify-content: center;
    }
}
</style>
